import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { UserRole, Permission } from '@/types';
import { hasPermission } from '@/types';

interface UserState {
  id: string | null;
  email: string | null;
  name: string | null;
  roles: UserRole[];
  isAuthenticated: boolean;
}

interface UserActions {
  setUser: (user: { id: string; email: string; name: string; roles: UserRole[] }) => void;
  clearUser: () => void;
  hasPermission: (permission: Permission) => boolean;
  hasRole: (role: UserRole) => boolean;
}

type UserStore = UserState & { actions: UserActions };

const initialState: UserState = {
  id: null,
  email: null,
  name: null,
  roles: [],
  isAuthenticated: false,
};

export const useUserStore = create<UserStore>()(
  devtools(
    immer((set, get) => ({
      ...initialState,
      actions: {
        setUser: (user) =>
          set((state) => {
            state.id = user.id;
            state.email = user.email;
            state.name = user.name;
            state.roles = user.roles;
            state.isAuthenticated = true;
          }),
        clearUser: () =>
          set((state) => {
            Object.assign(state, initialState);
          }),
        hasPermission: (permission: Permission) => {
          const { roles } = get();
          return roles.some((role) => hasPermission(role, permission));
        },
        hasRole: (role: UserRole) => {
          const { roles } = get();
          return roles.includes(role);
        },
      },
    })),
    { name: 'UserStore' }
  )
);

// Selectors
export const useUserId = () => useUserStore((state) => state.id);
export const useUserEmail = () => useUserStore((state) => state.email);
export const useUserName = () => useUserStore((state) => state.name);
export const useUserRoles = () => useUserStore((state) => state.roles);
export const useIsUserAuthenticated = () => useUserStore((state) => state.isAuthenticated);
export const useUserActions = () => useUserStore((state) => state.actions);
